import React, { useMemo } from "react";
import { FlightDetails, ConnectionFlight } from "../types";
import { FlightCardSkeleton } from "./SearchResultsSkeletons";
import FlightCard from "./FlightCard";
import ConnectionCard from "./ConnectionCard";
import StopsFilter from "./StopsFilter";
import { useFlightSelection } from "./useFlightSelection";
import { usePostRequest } from "./usePostRequest";

interface SearchResultsProps {
  flights: FlightDetails[];
  connectionFlights?: ConnectionFlight[];
  stopsFilter: "none" | "one-or-fewer" | "one" | "two" | "three" | null;
  onStopsFilterChange: (
    filter: "none" | "one-or-fewer" | "one" | "two" | "three" | null,
  ) => void;
  isLoadingFlights?: boolean;
  show: boolean;
  searchDate: string;
  selectedDates?: string[];
  // Receives optimistic "recently posted" cards built from the current
  // selection so the home view can show them instantly after posting.
  onPostRequestSuccess?: (optimisticItems?: any[]) => void;
  sourceLabel?: string;
  destinationLabel?: string;
  totalResults?: number;
  hideConnectedFilter?: boolean;
  hideInternalFilters?: boolean;
  readOnly?: boolean;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  flights,
  connectionFlights = [],
  stopsFilter,
  onStopsFilterChange,
  isLoadingFlights = false,
  show,
  searchDate,
  selectedDates = [],
  onPostRequestSuccess,
  sourceLabel,
  destinationLabel,
  totalResults = 0,
  hideConnectedFilter = false,
  hideInternalFilters = false,
  readOnly = false,
}) => {
  // Selection model + toggles (direct flights and connection segments).
  const {
    selectedFlights,
    selectedSegments,
    selectedCount,
    toggleFlightSelection,
    isSegmentSelected,
    areAllSegmentsSelected,
    toggleSegmentSelection,
    toggleAllSegmentsInConnection,
    clearSelection,
  } = useFlightSelection(stopsFilter, searchDate, selectedDates);

  // Post-request flow (validation, API, optimistic cards, credit refresh).
  const { posting, handlePostRequest } = usePostRequest({
    selectedFlights,
    selectedSegments,
    onPostRequestSuccess,
    clearSelection,
  });

  const d = new Date(searchDate);
  const weekday = d.toLocaleDateString(undefined, { weekday: "long" });
  const month = d.toLocaleDateString(undefined, { month: "long" });
  const day = d.getDate();
  const year = d.getFullYear();
  const formattedDate = `${month} ${day} ${year} - ${weekday}`;

  // Format selected dates for display
  const selectedDatesDisplay =
    selectedDates.length > 0
      ? selectedDates
          .map((date) => {
            const d = new Date(date);
            const weekday = d.toLocaleDateString(undefined, {
              weekday: "long",
            });
            const month = d.toLocaleDateString(undefined, { month: "long" });
            const day = d.getDate();
            const year = d.getFullYear();
            return `${month} ${day} ${year} - ${weekday}`;
          })
          .join(", ")
      : formattedDate;

  // Group flights by date for rendering date headers
  const flightsByDate = useMemo(() => {
    const groups: { date: string; label: string; flights: FlightDetails[] }[] =
      [];
    const dateMap = new Map<string, FlightDetails[]>();

    flights.forEach((flight) => {
      const dateKey =
        flight.scheduleDate ||
        flight.date ||
        flight.departureTime?.split("T")[0] ||
        "unknown";
      if (!dateMap.has(dateKey)) dateMap.set(dateKey, []);
      dateMap.get(dateKey)!.push(flight);
    });

    // Parse a date key into a Date. Keys can be ISO ("2026-06-29") or
    // human-readable ("July 1 2026") depending on the search path, so try the
    // local-midnight ISO form first, then fall back to a plain parse.
    const parseDateKey = (key: string): Date | null => {
      const iso = new Date(`${key}T00:00:00`);
      if (!isNaN(iso.getTime())) return iso;
      const loose = new Date(key);
      return isNaN(loose.getTime()) ? null : loose;
    };

    // Sort date keys chronologically. A plain string .sort() ordered them
    // lexicographically, which puts "July ..." before "June ..." (and any
    // unparseable key wherever its text falls); sort by the real date instead.
    const sortedKeys = Array.from(dateMap.keys()).sort((a, b) => {
      const da = parseDateKey(a);
      const db = parseDateKey(b);
      if (da && db) return da.getTime() - db.getTime();
      if (da) return -1; // unparseable keys sink to the bottom
      if (db) return 1;
      return a.localeCompare(b);
    });
    sortedKeys.forEach((dateKey) => {
      let label = dateKey;
      const d = parseDateKey(dateKey);
      if (d) {
        const month = d.toLocaleDateString("en-US", { month: "long" });
        const day = d.getDate();
        const year = d.getFullYear();
        const weekday = d.toLocaleDateString("en-US", { weekday: "long" });
        label = `${month} ${day} ${year} - ${weekday}`;
      }
      groups.push({ date: dateKey, label, flights: dateMap.get(dateKey)! });
    });

    return groups;
  }, [flights]);

  // Layout selection for the date-grouped cards:
  // - When a date has multiple flights (route search), keep each date as a
  //   full-width section with its flights in two columns -> uniform columns.
  // - When every date has a single flight (flight-number search across dates),
  //   pack the date groups two-per-row so the columns still fill up.
  const hasMultiFlightDate = flightsByDate.some((g) => g.flights.length > 1);

  // Group connection itineraries by departure date, mirroring flightsByDate, so
  // 1/2/3-stop and Connected results get the same date headers the non-stop
  // (direct) cards already have. Keyed off overallDepartureTime (falling back to
  // the first segment's departure) which is the trip's start date.
  const connectionsByDate = useMemo(() => {
    const dateMap = new Map<string, ConnectionFlight[]>();

    connectionFlights.forEach((conn) => {
      const dateKey =
        conn.overallDepartureTime?.split("T")[0] ||
        conn.segments[0]?.departureTime?.split("T")[0] ||
        "unknown";
      if (!dateMap.has(dateKey)) dateMap.set(dateKey, []);
      dateMap.get(dateKey)!.push(conn);
    });

    const parseDateKey = (key: string): Date | null => {
      const iso = new Date(`${key}T00:00:00`);
      if (!isNaN(iso.getTime())) return iso;
      const loose = new Date(key);
      return isNaN(loose.getTime()) ? null : loose;
    };

    const sortedKeys = Array.from(dateMap.keys()).sort((a, b) => {
      const da = parseDateKey(a);
      const db = parseDateKey(b);
      if (da && db) return da.getTime() - db.getTime();
      if (da) return -1;
      if (db) return 1;
      return a.localeCompare(b);
    });

    const groups: {
      date: string;
      label: string;
      connections: ConnectionFlight[];
    }[] = [];
    sortedKeys.forEach((dateKey) => {
      let label = dateKey;
      const d = parseDateKey(dateKey);
      if (d) {
        const month = d.toLocaleDateString("en-US", { month: "long" });
        const day = d.getDate();
        const year = d.getFullYear();
        const weekday = d.toLocaleDateString("en-US", { weekday: "long" });
        label = `${month} ${day} ${year} - ${weekday}`;
      }
      groups.push({ date: dateKey, label, connections: dateMap.get(dateKey)! });
    });

    return groups;
  }, [connectionFlights]);

  // The API total already reflects the active stops filter: data.total for the
  // direct "Non-stop" search, connectionTotal for any connection mode (1/2/3
  // Stops or "Connected"). It counts ALL pages, so adding this page's
  // connectionFlights on top double-counted every connection search. Use the
  // total as-is; only fall back to on-page array lengths when it's absent.
  const displayedResultCount =
    Number(totalResults) || flights.length + connectionFlights.length;

  const routeTitle =
    sourceLabel && destinationLabel
      ? `Search Results:  ${sourceLabel} to ${destinationLabel}`
      : sourceLabel
        ? `Search Results:  ${sourceLabel}`
        : "Search Results";

  if (!show) return null;

  // Show "No Data Found" only when not loading and no results
  const showNoData =
    !isLoadingFlights && flights.length === 0 && connectionFlights.length === 0;

  return (
    <section className="mt-6 searchResult px-1">
      <div className="search-results mt-4">
        {/* Two-column layout: Filters sidebar + Results */}
        <div className={`${hideInternalFilters ? "" : "flex gap-6"}`}>
          {/* Left Sidebar — Stops filter */}
          <StopsFilter
            stopsFilter={stopsFilter}
            onStopsFilterChange={onStopsFilterChange}
            displayedResultCount={displayedResultCount}
            hideInternalFilters={hideInternalFilters}
            hideConnectedFilter={hideConnectedFilter}
          />

          {/* Right Content - Results */}
          <div className="flex-1 min-w-0">
            <div className="border-b pb-3 sm:pb-4 mb-3 sm:mb-4">
              <p className="text-grease-100 text-base sm:text-lg font-semibold">
                {routeTitle}
              </p>
              <div className="flex items-center gap-2 mt-1.5 sm:mt-2 text-lightgray-200 text-xs sm:text-sm">
                <i className="st-calendar text-sm sm:text-base"></i>
                <span className="truncate">{selectedDatesDisplay}</span>
              </div>
            </div>
            {isLoadingFlights ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...Array(4)].map((_, index) => (
                  <FlightCardSkeleton key={index} />
                ))}
              </div>
            ) : showNoData ? (
              <div className="flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mb-3">
                  <svg
                    className="w-7 h-7 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                {stopsFilter === "none" ? (
                  <>
                    <p className="text-grease-100 text-base font-semibold">
                      No direct flights found
                    </p>
                    <p className="text-gray-400 text-sm mt-1 mb-4">
                      Try searching with a stopover to see more options.
                    </p>
                    <button
                      onClick={() => onStopsFilterChange("one")}
                      className="px-5 py-2.5 bg-[#FFBC00] hover:bg-[#e6a800] text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Search flights with one stop
                    </button>
                  </>
                ) : stopsFilter === "one-or-fewer" ? (
                  <>
                    <p className="text-grease-100 text-base font-semibold">
                      No flights found
                    </p>
                    <p className="text-gray-400 text-sm mt-1 mb-4">
                      We couldn't find any flights matching your filters.
                    </p>
                    <button
                      onClick={() => onStopsFilterChange("none")}
                      className="px-5 py-2.5 bg-[#FFBC00] hover:bg-[#e6a800] text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Reset to non-stop
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-grease-100 text-base font-semibold">
                      No flights found
                    </p>
                    <p className="text-gray-400 text-sm mt-1 mb-4">
                      We couldn't find any flights matching your filters.
                    </p>
                    <button
                      onClick={() => onStopsFilterChange("none")}
                      className="px-5 py-2.5 bg-[#FFBC00] hover:bg-[#e6a800] text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Reset to non-stop
                    </button>
                  </>
                )}
              </div>
            ) : (
              <>
                {/* Date-grouped flight cards. When dates each have one flight we
                    pack the groups two-per-row; otherwise each date is a
                    full-width section (its flights fill two columns below). */}
                <div
                  className={
                    hasMultiFlightDate
                      ? ""
                      : "grid grid-cols-1 md:grid-cols-2 gap-x-6 items-start"
                  }
                >
                  {flightsByDate.map((group) => (
                    <div key={group.date} className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <i className="st-calendar text-gray-400 text-sm"></i>
                        <p className="text-grease-100 text-sm font-medium">
                          {group.label}
                        </p>
                      </div>
                      <div
                        className={
                          hasMultiFlightDate
                            ? "grid grid-cols-1 md:grid-cols-2 gap-4"
                            : "grid grid-cols-1 gap-4"
                        }
                      >
                        {group.flights.map((flight) => {
                          const flightCompositeId =
                            flight.compositeId || String(flight.id);
                          const isSelected = selectedFlights.some(
                            (f) =>
                              (f.compositeId || String(f.id)) ===
                              flightCompositeId,
                          );
                          return (
                            <FlightCard
                              key={flightCompositeId}
                              flight={flight}
                              isSelected={isSelected}
                              readOnly={readOnly}
                              onSelect={toggleFlightSelection}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Connection flights — grouped by date, matching the layout of
                    the non-stop cards above. */}
                {connectionsByDate.map((group) => (
                  <div key={`conn-${group.date}`} className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <i className="st-calendar text-gray-400 text-sm"></i>
                      <p className="text-grease-100 text-sm font-medium">
                        {group.label}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {group.connections.map((connection, index) => (
                        <ConnectionCard
                          key={
                            connection.compositeId ||
                            `connection-${connection.segments[0]?.id}-${index}`
                          }
                          connection={connection}
                          index={index}
                          readOnly={readOnly}
                          isSegmentSelected={isSegmentSelected}
                          areAllSegmentsSelected={areAllSegmentsSelected}
                          toggleSegmentSelection={toggleSegmentSelection}
                          toggleAllSegmentsInConnection={
                            toggleAllSegmentsInConnection
                          }
                        />
                      ))}
                    </div>
                  </div>
                ))}

                {!readOnly &&
                  (selectedFlights.length > 0 ||
                    selectedSegments.length > 0) && (
                    <div className="flex justify-center">
                      <button
                        type="button"
                        disabled={posting}
                        className={`p-4 w-60 rounded-lg text-white text-center text-sm mt-6 bg-[#FFBC00] shadow-button transition-all ${
                          posting ? "opacity-70 cursor-not-allowed" : ""
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          handlePostRequest();
                        }}
                      >
                        {posting ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg
                              className="animate-spin h-4 w-4 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                              />
                            </svg>
                            Posting...
                          </span>
                        ) : (
                          <>
                            POST{" "}
                            {selectedFlights.length + selectedSegments.length}{" "}
                            REQUEST
                            {selectedFlights.length +
                              selectedSegments.length !==
                            1
                              ? "S"
                              : ""}
                          </>
                        )}
                      </button>
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SearchResults;
